import React, { DragEvent, useRef, useState } from 'react';
import CloudUploadIcon from '@mui/icons-material//CloudUpload';
import CircleIconWithText from 'components/CircleIconWithText';
import useHandleFiles from 'services/hooks/useHandleFiles';
import { DropZoneProps } from './DropZone.props';
import useStyles from './styles';

const DropZone = (props: DropZoneProps) => {
  const {
    setFileSrc,
    title = '',
    subTitle,
    acceptFileTypes,
    validFileTypes,
    maxSizeAllowedInMb,
    titleArray = [],
    fromDocumentFeature = false,
    fileId = null,
    allowMultipleFiles = true,
  } = props;
  const classes = useStyles();
  const fileInputRef = useRef<HTMLInputElement | null>(null);
  const { validateFiles } = useHandleFiles({
    validFileTypes,
    maxFileSizeAllowed: maxSizeAllowedInMb,
  });

  const [isOver, setIsOver] = useState(false);

  const preventDefault = (event: DragEvent<HTMLElement>): void => {
    event.preventDefault();
    event.stopPropagation();
  };

  const dragOver = (event: DragEvent<HTMLElement>): void => {
    preventDefault(event);
  };

  const dragEnter = (event: DragEvent<HTMLElement>): void => {
    preventDefault(event);
    setIsOver(true);
  };

  const dragLeave = (event: DragEvent<HTMLElement>): void => {
    preventDefault(event);
    setIsOver(false);
  };

  const handleFiles = (files: FileList) => {
    if (validateFiles(files)) {
      setFileSrc({
        // If we don't need to allow multiple files, we will only send the first file
        ...(allowMultipleFiles ? { files } : { file: files[0] }),
        // If we are uploading a file from the document feature, we will send the fileId
        ...(fromDocumentFeature ? { fileId } : {}),
      });
    }
  };

  const onDrop = (event: DragEvent<HTMLElement>): void => {
    setIsOver(false);
    preventDefault(event);
    const { files } = event.dataTransfer;
    if (files.length) {
      handleFiles(files);
    }
  };

  const filesSelected = () => {
    if (fileInputRef?.current?.files?.length) {
      handleFiles(fileInputRef.current.files);
    }
  };

  const fileInputClicked = () => {
    fileInputRef?.current?.click();
  };

  return (
    <div
      tabIndex={0}
      role="button"
      className={isOver ? classes.overContainer : classes.container}
      onDragOver={dragOver}
      onDragEnter={dragEnter}
      onDragLeave={dragLeave}
      onDrop={onDrop}
      onClick={fileInputClicked}
      onKeyDown={fileInputClicked}>
      <CircleIconWithText title={title} subTitle={subTitle} alternative titleArray={titleArray}>
        <CloudUploadIcon className={classes.cloudIconStyle} />
      </CircleIconWithText>
      <input
        ref={fileInputRef}
        className={classes.fileInput}
        type="file"
        accept={acceptFileTypes}
        multiple={allowMultipleFiles}
        onChange={filesSelected}
      />
    </div>
  );
};

export default DropZone;
